import React, { useState } from 'react'
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  X,
  Eye,
  MessageSquare,
  Clock,
  FileText,
  Flag,
  ThumbsUp,
  ThumbsDown,
  Search,
  Filter,
  ChevronDown
} from 'lucide-react'
import { mockDeliverables, mockCreators, mockCampaigns } from '../../data/mockData'

// Pending compliance reviews
const pendingReviews = [
  {
    id: '1',
    deliverable: {
      ...mockDeliverables[0],
      content: 'Check out this amazing new glucose monitor! It has completely changed how I manage my diabetes. #ad #sponsored',
      platform: 'instagram',
      type: 'reel'
    },
    creator: mockCreators[0],
    campaign: mockCampaigns[0],
    submittedAt: '2024-01-15T10:30:00Z',
    priority: 'high',
    flags: [
      { type: 'warning', message: 'Missing required FDA disclaimer' },
      { type: 'info', message: 'Claims require substantiation review' }
    ]
  },
  {
    id: '2',
    deliverable: {
      ...mockDeliverables[1],
      content: 'As a cardiologist, I recommend this heart health supplement to all my patients. The results speak for themselves!',
      platform: 'linkedin',
      type: 'article'
    },
    creator: mockCreators[1],
    campaign: mockCampaigns[1],
    submittedAt: '2024-01-15T09:15:00Z',
    priority: 'critical',
    flags: [
      { type: 'error', message: 'Unsubstantiated medical claim detected' },
      { type: 'error', message: 'Missing #ad disclosure' },
      { type: 'warning', message: 'Testimonial implies guarantee' }
    ]
  },
  {
    id: '3',
    deliverable: {
      ...mockDeliverables[2],
      content: 'Great experience at the MedTech conference! Excited to share what I learned about new innovations in patient care. #MedTechInnovation #HealthcareAI',
      platform: 'twitter',
      type: 'thread'
    },
    creator: mockCreators[2],
    campaign: mockCampaigns[2],
    submittedAt: '2024-01-15T08:00:00Z',
    priority: 'low',
    flags: [
      { type: 'info', message: 'Verify brand mention compliance' }
    ]
  }
]

const priorityConfig = {
  critical: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, label: 'Critical' },
  high: { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Flag, label: 'High' },
  medium: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'Medium' },
  low: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: FileText, label: 'Low' }
}

const flagColors = {
  error: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  info: 'bg-blue-50 border-blue-200 text-blue-700'
}

function AdminCompliance() {
  const [selectedReview, setSelectedReview] = useState(null)
  const [reviewNote, setReviewNote] = useState('')
  const [filterPriority, setFilterPriority] = useState('all')

  const filteredReviews = filterPriority === 'all'
    ? pendingReviews
    : pendingReviews.filter(r => r.priority === filterPriority)

  const handleApprove = (reviewId) => {
    alert(`Content approved for review ${reviewId}`)
    setSelectedReview(null)
    setReviewNote('')
  }

  const handleReject = (reviewId) => {
    if (!reviewNote.trim()) {
      alert('Please provide feedback for the creator')
      return
    }
    alert(`Content rejected for review ${reviewId} with feedback: ${reviewNote}`)
    setSelectedReview(null)
    setReviewNote('')
  }

  const handleRequestRevision = (reviewId) => {
    if (!reviewNote.trim()) {
      alert('Please provide revision instructions')
      return
    }
    alert(`Revision requested for review ${reviewId}`)
    setSelectedReview(null)
    setReviewNote('')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Review</h1>
          <p className="text-gray-500">Review and approve creator content for regulatory compliance</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search reviews..."
              className="input pl-10 w-64"
            />
          </div>
          <div className="relative">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="input appearance-none pr-10"
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Reviews</p>
              <p className="text-2xl font-bold text-gray-900">{pendingReviews.length}</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Critical Issues</p>
              <p className="text-2xl font-bold text-red-600">
                {pendingReviews.filter(r => r.priority === 'critical').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Approved Today</p>
              <p className="text-2xl font-bold text-green-600">12</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg. Review Time</p>
              <p className="text-2xl font-bold text-gray-900">2.4h</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Review Queue */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Review Queue</h2>
        <div className="space-y-4">
          {filteredReviews.map((review) => {
            const PriorityIcon = priorityConfig[review.priority].icon
            return (
              <div
                key={review.id}
                className={`p-4 rounded-xl border-2 ${
                  selectedReview?.id === review.id
                    ? 'border-hf-teal-500 bg-hf-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Creator Info */}
                  <img
                    src={review.creator.avatar}
                    alt={review.creator.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{review.creator.name}</h3>
                          <span className={`badge ${priorityConfig[review.priority].color}`}>
                            <PriorityIcon className="w-3 h-3 mr-1" />
                            {priorityConfig[review.priority].label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {review.campaign.name} • {review.deliverable.platform} {review.deliverable.type}
                        </p>
                      </div>
                      <span className="text-sm text-gray-400">
                        {new Date(review.submittedAt).toLocaleString()}
                      </span>
                    </div>

                    {/* Content Preview */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm text-gray-700">{review.deliverable.content}</p>
                    </div>

                    {/* Flags */}
                    <div className="space-y-2 mb-3">
                      {review.flags.map((flag, index) => (
                        <div
                          key={index}
                          className={`flex items-start gap-2 p-2 rounded-lg border ${flagColors[flag.type]}`}
                        >
                          {flag.type === 'error' && <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                          {flag.type === 'warning' && <Flag className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                          {flag.type === 'info' && <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                          <span className="text-sm">{flag.message}</span>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedReview(selectedReview?.id === review.id ? null : review)}
                        className="btn-secondary text-sm py-1.5"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {selectedReview?.id === review.id ? 'Hide Details' : 'Review'}
                      </button>
                      <button
                        onClick={() => handleApprove(review.id)}
                        className="btn-primary text-sm py-1.5 bg-green-600 hover:bg-green-700"
                      >
                        <ThumbsUp className="w-4 h-4 mr-1" />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setSelectedReview(review)
                          setReviewNote('')
                        }}
                        className="btn-secondary text-sm py-1.5 text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <ThumbsDown className="w-4 h-4 mr-1" />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Review Panel */}
                {selectedReview?.id === review.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-6">
                      {/* Compliance Checklist */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Compliance Checklist</h4>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300" />
                            <span className="text-sm text-gray-700">FTC disclosure present and visible</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300" />
                            <span className="text-sm text-gray-700">No unsubstantiated medical claims</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300" />
                            <span className="text-sm text-gray-700">FDA disclaimer included (if required)</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300" />
                            <span className="text-sm text-gray-700">Brand guidelines followed</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" className="rounded border-gray-300" />
                            <span className="text-sm text-gray-700">HIPAA compliant (no patient info)</span>
                          </label>
                        </div>
                      </div>

                      {/* Review Notes */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Review Notes</h4>
                        <textarea
                          value={reviewNote}
                          onChange={(e) => setReviewNote(e.target.value)}
                          placeholder="Add feedback or revision instructions for the creator..."
                          className="input h-32 resize-none"
                        />
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => handleRequestRevision(review.id)}
                            className="btn-secondary text-sm"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Request Revision
                          </button>
                          <button
                            onClick={() => handleReject(review.id)}
                            className="btn-secondary text-sm text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {filteredReviews.length === 0 && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
          <p className="text-gray-500">No pending compliance reviews at this priority level.</p>
        </div>
      )}
    </div>
  )
}

export default AdminCompliance
